import { Search, RotateCcw } from "lucide-react";

// ─── constants ───────────────────────────────────────────────────────────────

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const now = new Date();
const currentYear = now.getFullYear();

const YEARS = Array.from(
  { length: currentYear - 2023 },
  (_, i) => currentYear - i
);

// ─── component ───────────────────────────────────────────────────────────────

/**
 * Reusable filter bar for admin pages.
 *
 * Props:
 * - filters: object with current filter values
 * - onFilterChange: (key, value) => void
 * - onReset: () => void
 * - dropdowns: { districts, talukas, categories, subCategories, subCategoriesLoading }
 * - showFields: array of field names to show. Options:
 *   "year", "month", "status", "district", "taluka", "category", "sub_category", "search"
 * - statusOptions: array of { value, label } for status dropdown (optional)
 * - searchPlaceholder: string (optional)
 */
export default function AdminFilterBar({
  filters = {},
  onFilterChange,
  onReset,
  dropdowns = {},
  showFields = ["year", "month", "district", "taluka", "category", "sub_category"],
  statusOptions = [],
  searchPlaceholder = "Business name or email…",
}) {
  const { districts = [], talukas = [], categories = [], subCategories = [], subCategoriesLoading = false } = dropdowns;

  const show = (field) => showFields.includes(field);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-7 gap-3 items-end">
      {/* Year */}
      {show("year") && (
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Year</label>
          <select
            value={filters.year || ""}
            onChange={(e) => onFilterChange("year", Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[90px]"
          >
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      )}

      {/* Month */}
      {show("month") && (
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Month</label>
          <select
            value={filters.month || ""}
            onChange={(e) => onFilterChange("month", Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[120px]"
          >
            {MONTHS.filter((m) => {
              if ((filters.year || currentYear) < currentYear) return true;
              return m.value <= now.getMonth() + 1;
            }).map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* Status */}
      {show("status") && statusOptions.length > 0 && (
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Status</label>
          <select
            value={filters.status || "all"}
            onChange={(e) => onFilterChange("status", e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[100px]"
          >
            {statusOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      )}

      {/* District */}
      {show("district") && (
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-semibold uppercase tracking-wide">District</label>
          <select
            value={filters.district_id || ""}
            onChange={(e) => onFilterChange("district_id", e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[120px]"
          >
            <option value="">All Districts</option>
            {districts.map((d) => (
              <option key={d.uuid} value={d.uuid}>{d.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Taluka */}
      {show("taluka") && filters.district_id && (
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Taluka</label>
          <select
            value={filters.taluka_id || ""}
            onChange={(e) => onFilterChange("taluka_id", e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[120px]"
          >
            <option value="">All Talukas</option>
            {talukas.map((t) => (
              <option key={t.uuid} value={t.uuid}>{t.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Category */}
      {show("category") && (
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Category</label>
          <select
            value={filters.category_id || ""}
            onChange={(e) => onFilterChange("category_id", e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[120px]"
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c.uuid} value={c.uuid}>{c.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Sub-Category */}
      {show("sub_category") && filters.category_id && (
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Sub-Category</label>
          <select
            value={filters.sub_category_id || ""}
            onChange={(e) => onFilterChange("sub_category_id", e.target.value)}
            disabled={subCategoriesLoading}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[120px] disabled:opacity-50"
          >
            <option value="">All Sub-Categories</option>
            {subCategories.map((sc) => (
              <option key={sc.uuid} value={sc.uuid}>{sc.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Search + Reset */}
      {show("search") ? (
        <div className="flex flex-col gap-1 col-span-2 sm:col-span-1">
          <label className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Search</label>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={filters.search || ""}
                onChange={(e) => onFilterChange("search", e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
              />
            </div>
            <button
              onClick={onReset}
              className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
              title="Reset filters"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={onReset}
          className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors self-end justify-self-start"
          title="Reset filters"
        >
          <RotateCcw className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export { MONTHS, YEARS };
